import {Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output} from '@angular/core';
import {Dossiers} from '../../../common/services';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {debounceTime, distinctUntilChanged, filter} from 'rxjs/operators';

import * as moment from 'moment';
import {DarDossiersService} from '../../../common/services/dossiers/dossiers.service';
import {RemunerationEnum} from '../../../common/services/enums/remunerationEnum';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import {DatePipe} from '@angular/common';
import {Subscription} from 'rxjs';
import {DateHelperService} from "app/dar/common/services/date-helper/date-helper.service";

@Component({
  selector: 'app-periode-commission',
  templateUrl: './periode-commission.component.html',
  styleUrls: ['./periode-commission.component.scss']
})
export class PeriodeCommissionComponent implements OnInit, OnDestroy {
  @Input() dossier: Dossiers = null;

  @Output() periodeUpdated = new EventEmitter<any>();

  cout = 0.0;
  cout_estime = 0.0;

  /**
   * la date minimum pour la date de fin (>= date début)
   */
  minDate = null;

  periodeFormGroup: UntypedFormGroup;

  remunerationEnum = RemunerationEnum;

  subs: Subscription[] = [];

  constructor(@Inject(LOCALE_ID) private locale: string,
              public dossierSvc: DarDossiersService,
              public dateSvc: DateHelperService,
              private formBuilder: UntypedFormBuilder) {
    this._initForm();
    this._listenValues();
  }

  ngOnInit() {
    if (this.dossier) {
      this._setValues(this.dossier);
    }
  }

  private _initForm() {
    this.periodeFormGroup = this.formBuilder.group({
      remuneration: [],
      date_debut_validee: [],
      date_fin_validee: [],
      etp: new UntypedFormControl({value: '', disabled: true}),
      duree_validee: new UntypedFormControl({value: '', disabled: true}),
      quotite_validee: [0, Validators.max(100)],
      vacation_horaires_validee: ['', Validators.pattern('^\\d+$')]
    });
  }

  private _listenValues() {
    this.subs.push(this.periodeFormGroup.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(),
      // .pipe(debounceTime(1000),
        filter((_form) => {
          if ((_form.remuneration === RemunerationEnum.indiciaire && _form.quotite_validee > 0) ||
              (_form.remuneration === RemunerationEnum.horaire && _form.vacation_horaires_validee > 0)) {
            return true;
          }

          return false;
        })
      )
      .subscribe((data) => {
        // console.log(data);

        this.calcul_duree(data.date_debut_validee, data.date_fin_validee);

        if (this.dossier.remuneration === RemunerationEnum.indiciaire) {
          const cout_duree = this.calcul_cout_duree_commission();
          const cout = this.calcul_cout_quotite_commission(cout_duree, data.quotite_validee);
          this.calcul_etp();
          // console.log('indicaire - cout ', this.cout);
          // console.log('indicaire - cout ', cout);
        }

        if (this.dossier.remuneration === RemunerationEnum.horaire) {
          const cout = this.calcul_horaire(data.vacation_horaires_validee);
          // console.log('horaire - cout ', this.cout);
          // console.log('horaire - cout ', cout);
        }

        const data_emit = {
          cout_valide: this.cout,
          date_debut_validee: data.date_debut_validee,
          date_fin_validee: data.date_fin_validee,
          quotite_validee: data.quotite_validee,
          vacation_horaires_validee: data.vacation_horaires_validee
        };

        // console.log(data_emit);

        this.periodeUpdated.emit(data_emit);
      }));
  }

  getInitData() {
    const cout_a_100 = this.calcul_cout_a_100(this.dossier.cout_estime);

    const dp: DatePipe = new DatePipe(this.locale);
    return `coût à 100 % : ${cout_a_100} € - du ${dp.transform(this.dossier.date_debut, 'dd/MM/yyyy')} au ${dp.transform(this.dossier.date_fin, 'dd/MM/yyyy')}`;
  }

  /**
   * On ramène le cout à une quotité de 100 % le cas échéant
   * @param cout
   */
  calcul_cout_a_100(cout) {
    let cout_a_100 = cout;

    if (this.dossier.quotite < 100) {
      cout_a_100 = (cout_a_100 * 100) / this.dossier.quotite;
    }

    return cout_a_100;
  }

  /**
   * Calcul cout pour le volume horaires changé
   * @param nouveaux_horaires
   */
  calcul_horaire(nouveaux_horaires) {
    this.cout = this._arrondi((nouveaux_horaires * this.cout_estime) / this.dossier.vacation_horaires);

    return this.cout;
  }

  /**
   * calcul de la durée entre 2 dates, dates incluses
   * mise à jour affichage duree_validee
   * @param dt_debut
   * @param dt_fin
   */
  calcul_duree(dt_debut, dt_fin) {
    this.dossier.duree_validee = this.dossierSvc.getDaysBetweenDates(dt_debut, dt_fin);
    this.periodeFormGroup.patchValue({duree_validee: this.dossier.duree_validee});
  }

  /**
   * Calcul cout selon la durée qui change
   * Produit en X pour trouver le nouveau cout :
   * duree (initiale) => cout estimé
   * duree commission (changement de dates) => N nouveau cout
   */
  calcul_cout_duree_commission() {
    return (this.dossier.duree_validee * this.dossier.cout_estime) / this.dossier.duree;
  }

  /**
   * Calcul ETP et affichage
   * 1 ETP => 365 jours, pondéré avec la quotité, arrondi à la 4eme décimale
   */
  calcul_etp() {
    const etp_brut = this.dossier.duree_validee / 365;
    const etp = etp_brut * (this.dossier.quotite_validee / 100);
    // const etp_arrondi = Math.round(parseFloat(etp) * 100) / 100;
    const etp_arrondi = Number(etp).toFixed(4);
    // console.log(etp_arrondi);

    // this.periodeFormGroup.patchValue({etp: etp});
    this.periodeFormGroup.patchValue({etp: etp_arrondi});

    return etp;
  }

  /**
   * Cout arrondi en chiffre entier
   * @param cout
   * @private
   */
  _arrondi(cout) {
    // console.log('cout à arrondir ', cout);
    const arrondi = Number(Number(cout).toFixed(0));
    // console.log('arrondi ', arrondi);
    return arrondi;
  }

  /**
   * Calcul cout comission pondérée avec la quotité modifiée
   * @param cout_duree
   * @param quotite
   */
  calcul_cout_quotite_commission(cout_duree, quotite) {
    // console.log(`cout à calculer avec cout_duree ${cout_duree} et quotité ${quotite}`);
    const cout = (this.calcul_cout_a_100(cout_duree) * quotite) / 100;

    // console.log('nouveau cout calculé ', cout);
    // console.log('cout arrondi ', this._arrondi(cout));

    this.cout = this._arrondi(cout);

    return this.cout;
    // console.log('cout calcule this.cout ', this.cout);
  }

  /**
   * Dossier => Form valeurs
   * @param dossier
   * @private
   */
  private _setValues(dossier: Dossiers) {
    const dtDebut = this.getDateFromString(dossier.date_debut_validee || dossier.date_debut);
    const dtFin = this.getDateFromString(dossier.date_fin_validee || dossier.date_fin);

    this.cout_estime = this.dossier.cout_estime;

    this.periodeFormGroup.patchValue({
      cout_estime: dossier.cout_estime,
      cout_valide: dossier.cout_valide,
      remuneration: dossier.remuneration,
      date_debut_validee: dtDebut,
      date_fin_validee: dtFin,
      quotite_validee: dossier.quotite_validee || dossier.quotite,
      vacation_horaires_validee: dossier.vacation_horaires_validee || dossier.vacation_horaires
    });
    const nbDays = this.dossierSvc.getDaysBetweenDates(dtDebut, dtFin);
    if (nbDays > 0) {
      this.periodeFormGroup.patchValue({duree_validee: nbDays});
    }
  }

  /**
   * Transformation Date string (YYYY-MM-DD => Date())
   * TODO : peut-on ne pas utiliser moment ?
   * @param dt
   */
  private getDateFromString(dt: string) {
    let dtReturn = null;
    if (dt) {
      dtReturn = moment(dt).toDate();
    }
    return dtReturn;
  }

  /**
   * soit dte du dateChange($event) ou appel explicite avec une date
   * @param dte
   * @param {string} control : le contrôle appelant (date début ou date fin)
   */
  onDateSelected(dte: MatDatepickerInputEvent<Date>, control = '') {
    // console.log('date selected ', dte);

    if (control === 'debut') {
      this.minDate = dte.value;
    }

    const dateDebut = this.periodeFormGroup.controls['date_debut_validee'].value;
    const dateFin = this.periodeFormGroup.controls['date_fin_validee'].value;
    if (dateDebut && dateFin) {
      const nbDays = this.dossierSvc.getDaysBetweenDates(dateDebut, dateFin);
      if (nbDays > 0) {
        this.periodeFormGroup.patchValue({duree_validee: nbDays});
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(_sub => _sub.unsubscribe());
  }
}
